import { useCallback, useEffect, useMemo, useState } from "react";
import type { SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { SortableTableHead } from "@trillion/ui/components/admin/sortable-header";
import { SORTABLE_GRID_OPTIONS, orderByKey } from "@trillion/ui/lib/data-grid-sort";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { settingsApi } from "@/lib/admin-api";
import { getSettingsColumns } from "@/components/settings/settings-columns";
import { SettingsDialog } from "@/components/settings/settings-dialog";
import { ConvertToSecretDialog } from "@/components/settings/convert-to-secret-dialog";
import { GridFilterInput, NO_MATCHES_TEXT } from "@/components/grid-filter-input";
import type { SettingView } from "@/lib/settings-types";
import { filterByWildcard } from "@/lib/wildcard-match";

// Settings are configuration, not job state — this page does not poll.
export const SETTINGS_DEFAULT_SORT: SortingState = [{ id: "scope", desc: false }];

/** Composite domain key. The create schema bounds both segments to charsets without `/`, so it is
 *  unambiguous. */
function settingKey(s: SettingView): string {
  return `${s.scope}/${s.key}`;
}

export function SettingsPage() {
  const [data, setData] = useState<SettingView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SettingView | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<SettingView | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [convertTarget, setConvertTarget] = useState<SettingView | null>(null);
  const [sorting, setSorting] = useState<SortingState>(SETTINGS_DEFAULT_SORT);
  // The type-to-filter pattern (sc-114202): client-side over the fetched rows, never written to the URL, and
  // kept across the reload after a save or delete.
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await settingsApi.list());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = useCallback(() => {
    setEditing(undefined);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((s: SettingView) => {
    setEditing(s);
    setDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await settingsApi.remove(deleteTarget.scope, deleteTarget.key);
      toast.success(`Deleted "${deleteTarget.key}"`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete setting");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, load]);

  const columns = useMemo(
    () =>
      getSettingsColumns({
        onEdit: openEdit,
        onDelete: setDeleteTarget,
        onConvertToSecret: setConvertTarget,
      }),
    [openEdit]
  );

  // A row shows when its scope OR its key matches; the value column is never searched.
  const filtered = useMemo(
    () => filterByWildcard(data, filter, (s) => [s.scope, s.key]),
    [data, filter]
  );

  // Normalized base order, so the tie-break for equal or missing values IS the composite key.
  const rows = useMemo(() => orderByKey(filtered, settingKey), [filtered]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...SORTABLE_GRID_OPTIONS,
    getRowId: settingKey,
    state: { sorting },
    onSortingChange: setSorting,
  });

  return (
    <div className="flex flex-col">
      <AdminHeader
        title="Settings"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Settings" }]}
      />
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="Conduit Settings"
          description="Global and job-level configuration — secrets are encrypted at rest and masked here"
          actions={
            <Button onClick={openCreate}>
              <Plus className="size-4" />
              Add setting
            </Button>
          }
        />

        <DataTableWrapper
          title="Settings"
          description="Global and job-level settings"
          isLoading={isLoading}
          toolbar={
            <GridFilterInput
              value={filter}
              onChange={setFilter}
              label="Filter settings"
              placeholder="Filter by scope or key (* = any text)"
            />
          }
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHead key={header.id} header={header} />
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {data.length > 0 ? NO_MATCHES_TEXT : "No settings yet."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DataTableWrapper>
      </div>

      <SettingsDialog
        editing={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={load}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete setting"
        description={`Delete ${deleteTarget?.scope ?? ""} / ${deleteTarget?.key ?? ""}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        variant="destructive"
        isLoading={isDeleting}
      />
      <ConvertToSecretDialog
        target={convertTarget}
        onClose={() => setConvertTarget(null)}
        onConverted={load}
      />
    </div>
  );
}
